import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { 
  FaFolderOpen, 
  FaDownload,
  FaFileAlt,
  FaUserTie,
  FaHandshake,
  FaShieldAlt,
  FaFileContract,
  FaUsers,
  FaUserMinus,
  FaClipboardList
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const EmployeeMaterials = ({ embedded = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleBack = () => {
    navigate('/portal/hr/materials');
  };

  const handleDownload = (documentName) => {
    // Find the document in our fetched documents
    const doc = documents.find(d => d.title === documentName);
    if (doc && doc.fileUrl) {
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = doc.fileUrl;
      link.download = doc.fileName || doc.title;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.log(`No file available for ${documentName}`);
      // In a real implementation, you might want to show an alert or notification
    }
  };

  // Fetch documents from Firebase
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'templatesCollections'));
        const docs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docs);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const hiringResources = [
    {
      name: 'Background Check Consent Form',
      description: 'Required consent form for conducting pre-employment background checks',
      icon: <FaShieldAlt className="h-6 w-6" />,
      documentTitle: 'Background Check'
    },
    {
      name: 'Interview Evaluation Form',
      description: 'Standardized form for evaluating candidate performance during interviews',
      icon: <FaClipboardList className="h-6 w-6" />,
      documentTitle: 'Interview Evaluation'
    },
    {
      name: 'Offer Letter',
      description: 'Professional offer letter template for extending employment offers',
      icon: <FaFileContract className="h-6 w-6" />,
      documentTitle: 'Offer Letter'
    },
    {
      name: 'Employment Application',
      description: 'Standard employment application form for job candidates',
      icon: <FaFileAlt className="h-6 w-6" />,
      documentTitle: 'Employment Application'
    }
  ];

  const terminationResources = [
    {
      name: 'Exit Interview Form',
      description: 'Comprehensive form for conducting exit interviews with departing employees',
      icon: <FaUserMinus className="h-6 w-6" />,
      documentTitle: 'Exit Interview Form'
    }
  ];

  // Helper function to check if a document has a file available
  const hasFileAvailable = (documentTitle) => {
    const doc = documents.find(d => d.title === documentTitle);
    return doc && doc.fileUrl;
  };

  if (loading) {
    return (
      <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
        {!embedded && <PortalBackground />}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <BBALoader size={48} className="mx-auto mb-4" />
              <p className="text-lg">Loading employee materials...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        {!embedded && (
          <div className="flex items-center justify-between mb-8 relative">
            <div className="flex-1 flex items-center">
              <BackButton onClick={handleBack} />
            </div>
            <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
              <FaFolderOpen className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Hiring Materials</h1>
            </div>
            <div className="flex-1 flex justify-end">
            </div>
          </div>
        )}

        {/* Hiring Resources Section */}
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <FaUserTie className={`text-2xl mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h2 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              Hiring Resources
            </h2>
            <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${
              darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
            }`}>
              For HR & Hiring Managers
            </span>
          </div>
          <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Essential documents and forms for managing the hiring process, from initial application to offer letter.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hiringResources.map((resource, index) => (
              <div key={index} className={`p-6 rounded-lg shadow-md border ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } hover:shadow-lg transition-shadow duration-200`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg mr-4 ${
                      darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {resource.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {resource.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {resource.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleDownload(resource.documentTitle)}
                    disabled={!hasFileAvailable(resource.documentTitle)}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasFileAvailable(resource.documentTitle)
                        ? darkMode 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                        : darkMode
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FaDownload className="inline mr-2" />
                    {hasFileAvailable(resource.documentTitle) ? 'Download' : 'File Not Available'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Termination Section */}
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <FaUserMinus className={`text-2xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <h2 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              Termination
            </h2>
            <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${
              darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
            }`}>
              For HR & Managers
            </span>
          </div>
          <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Required documentation and forms for managing employee terminations and exit processes.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {terminationResources.map((resource, index) => (
              <div key={index} className={`p-6 rounded-lg shadow-md border ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } hover:shadow-lg transition-shadow duration-200`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg mr-4 ${
                      darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-600'
                    }`}>
                      {resource.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {resource.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {resource.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleDownload(resource.documentTitle)}
                    disabled={!hasFileAvailable(resource.documentTitle)}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasFileAvailable(resource.documentTitle)
                        ? darkMode 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-red-600 text-white hover:bg-red-700'
                        : darkMode
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FaDownload className="inline mr-2" />
                    {hasFileAvailable(resource.documentTitle) ? 'Download' : 'File Not Available'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeMaterials; 